/**
 * Safe wrappers for localStorage and sessionStorage.
 *
 * In private browsing mode, iframe embeds, and some iOS Safari versions,
 * accessing Web Storage throws a SecurityError. These helpers catch that
 * error and return sensible defaults so the app never crashes.
 */

function isStorageAvailable(storage: Storage): boolean {
  const testKey = "__storage_test__"
  try {
    storage.setItem(testKey, "1")
    storage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

const createSafeStorage = (getStorage: () => Storage) => ({
  getItem(key: string): string | null {
    try {
      return getStorage().getItem(key)
    } catch {
      return null
    }
  },

  setItem(key: string, value: string): void {
    try {
      getStorage().setItem(key, value)
    } catch {
      // Storage full or blocked – silently ignore
    }
  },

  removeItem(key: string): void {
    try {
      getStorage().removeItem(key)
    } catch {
      // Storage blocked – silently ignore
    }
  },

  isAvailable(): boolean {
    try {
      return isStorageAvailable(getStorage())
    } catch {
      return false
    }
  }
})

export const safeLocalStorage = createSafeStorage(() => window.localStorage)
export const safeSessionStorage = createSafeStorage(() => window.sessionStorage)
